import React from "react";
import {
  EmptyState,
  EmptyStateProps,
  LoadPhase,
  NewTag,
  OneLineList,
  RowActionsMenu,
  SkeletonCard,
  TABLE_BODY_SKELETON,
} from "../../../components/ui";
import { useOpenedEnquiries } from "../hooks/useOpenedEnquiries";
import { isNewEnquiry } from "../openedEnquiries";
// The same tick and cross a client's payment history answers a request with —
// the same act on the same record, so the same control. See `PaymentAnswer`.
import PaymentAnswer from "../../payments/components/PaymentAnswer";
import type { GateProps } from "../../billing/hooks/useSubscriptionGate";
import { formatPrice } from "../../brand/planFormat";
import type { Enquiry, EnquiryStatus } from "../types/enquiries.types";

const STATUS_STYLES: Record<EnquiryStatus, string> = {
  "Recently Added": "bg-[#EFFBFD] text-[#007E8C]",
  Interested: "bg-[#ECFFEB] text-[#1A8810]",
  "Not interested": "bg-[#F9F9FB] text-[#62636C]",
  "Payment sent": "bg-[#FFF7D9] text-[#917A2E]",
  // The app's success green, deeper than the one "Interested" wears: this is
  // the money in rather than a good sign, and the two sit in the same column.
  Converted: "bg-[#E7F8EF] text-[#2F8E5B]",
};

/**
 * What the cell says once its request has been called off.
 *
 * Not a stage, which is why it is not in the table above: declining a payment
 * deliberately leaves the enquiry where it stood — they may pay another way, or
 * be asked again — so the row is still at "Payment sent" and the column would
 * otherwise go back to saying so, over a request nobody is waiting on any more.
 */
const DECLINED_STYLE = "bg-[#F9F9FB] text-[#62636C]";

/**
 * Whether the row is a question rather than a state: a request has gone out and
 * nothing has come back about it.
 *
 * A payment the row has never seen counts as outstanding — the list need not
 * carry one, and "Payment sent" is the backend saying a request is open. The
 * one it was raised against is fetched when the tick is pressed, and an answer
 * already given elsewhere is caught there. See `Enquiry`.
 */
/**
 * The "New" flag on a row nobody has opened or moved yet, and nothing at all
 * once somebody has — see `isNewEnquiry` for what counts.
 *
 * It subscribes for itself rather than being handed a set from above, because
 * half of what it is drawn from is not part of the enquiry: opened-ness is
 * this browser's bookkeeping, kept in `openedEnquiries`, and threading it
 * through the table would put a device-local fact in the same signature as
 * the rows the server sent. Ten rows is ten subscribers to one module store,
 * which is what `useSyncExternalStore` is cheap at.
 *
 * The tag goes as soon as the detail screen loads — the row is still on the
 * list behind it, and it is updated in place rather than on the way back.
 */
const RowNewTag: React.FC<{ enquiry: Enquiry }> = ({ enquiry }) => {
  const opened = useOpenedEnquiries();
  return isNewEnquiry(enquiry, opened) ? <NewTag>New</NewTag> : null;
};

const isAwaitingAnswer = (enquiry: Enquiry): boolean =>
  enquiry.status === "Payment sent" &&
  (enquiry.payment?.status ?? "Pending") === "Pending";

// Avatar tints, picked per name so the same enquiry always gets the same
// colour without storing one on the record.
const AVATAR_TINTS = [
  "bg-[#DDF3E4] text-[#2F8E5B]",
  "bg-[#DEEBFB] text-[#2F6BAE]",
  "bg-[#EDE7F6] text-[#5B3FA8]",
  "bg-[#FDF0DC] text-[#B4761A]",
  "bg-[#E6FAFB] text-[#00899B]",
];

const tintFor = (name: string) => {
  const sum = name
    .split("")
    .reduce((total, char) => total + char.charCodeAt(0), 0);
  return AVATAR_TINTS[sum % AVATAR_TINTS.length];
};

const COLUMNS = [
  "Client name",
  "Goal",
  "Mode",
  "Requested on",
  "Days ago",
  "Status",
];

interface EnquiriesTableProps {
  enquiries: Enquiry[];
  /** Stands in for the table body when the list comes back with no rows. */
  empty: EmptyStateProps;
  /**
   * Where the list is. While it is on its way the body says nothing — no rows
   * and no message — and only a load slow enough to notice shimmers, as one
   * block the size of the table. Rows already in hand are left alone either
   * way: a table with something to show goes on showing it while the next
   * answer is fetched.
   */
  phase?: LoadPhase;
  onSelectEnquiry: (enquiry: Enquiry) => void;
  /** Opens the Change status dialog on this row. */
  onChangeStatus: (enquiry: Enquiry) => void;
  /**
   * What Change status wears while the workspace has no plan behind it. Its own
   * rather than `answerGate` below, because it is a different act and says so:
   * moving an enquiry along the pipeline, not settling the money on one.
   *
   * Worked out once by the screen above, since every row carries the same one —
   * see `AllEnquiriesPage`.
   */
  statusGate: GateProps;
  /**
   * What the two answers wear while the workspace has no plan behind it.
   * Worked out once by the screen above, since every outstanding row carries
   * the same one — see `AllEnquiriesPage`.
   */
  answerGate: GateProps;
  /**
   * The row whose request is being fetched, if any: pressing a tick reads the
   * payment behind it before the question can be asked, and the icon spins for
   * that moment rather than looking untouched.
   */
  answering?: string | null;
  /** Asks whether the money for this row's request came in. */
  onConfirmPayment: (enquiry: Enquiry) => void;
  /** Asks whether this row's request should be called off. */
  onDeclinePayment: (enquiry: Enquiry) => void;
}

const EnquiriesTable: React.FC<EnquiriesTableProps> = ({
  enquiries,
  empty,
  phase = "ready",
  onSelectEnquiry,
  onChangeStatus,
  statusGate,
  answerGate,
  answering = null,
  onConfirmPayment,
  onDeclinePayment,
}) =>
  phase !== "ready" && enquiries.length === 0 ? (
    <div className="flex-1 min-h-0 p-4 sm:p-6">
      <SkeletonCard phase={phase} className={TABLE_BODY_SKELETON} />
    </div>
  ) : (
  // The only scroll region on the page: rows move, the sticky header row and
  // the card's pagination footer stay put.
  <div className="flex-1 min-h-0 overflow-auto custom-scrollbar-overlay">
    {/* Full height only when the rows are gone: an empty table that stops at
        its header leaves the state stranded at the top of the card with a
        screenful of nothing under it, so the body takes the whole scroll
        region and the one cell in it centres in that. With rows to show the
        table sizes to them, or every row would stretch to share the height. */}
    <table
      className={`w-full min-w-[860px] table-fixed border-collapse text-left ${
        enquiries.length === 0 ? "h-full" : ""
      }`}
    >
      <colgroup>
        <col className="w-[26%]" />
        <col className="w-[16%]" />
        <col className="w-[14%]" />
        <col className="w-[16%]" />
        <col className="w-[13%]" />
        <col className="w-[15%]" />
      </colgroup>
      <thead className="sticky top-0 z-10">
        <tr>
          {COLUMNS.map((heading) => (
            <th
              key={heading}
              scope="col"
              className="bg-white px-4 sm:px-6 py-3 text-[13px] font-normal text-ink-muted shadow-[inset_0_-1px_0_#E9EDF1]"
            >
              {heading}
            </th>
          ))}
        </tr>
      </thead>
      {enquiries.length === 0 ? (
        <tbody>
          <tr>
            <td colSpan={COLUMNS.length} className="align-middle">
              <EmptyState {...empty} />
            </td>
          </tr>
        </tbody>
      ) : (
        <tbody>
          {enquiries.map((enquiry) => {
            const awaiting = isAwaitingAnswer(enquiry);
            // Named in the answers' labels where the row knows it, since an
            // icon has no words of its own and the amount is what makes it
            // clear which request is being settled.
            const asked = enquiry.payment
              ? ` ${formatPrice(enquiry.payment.amount)}`
              : "";
            // Only where the stage is still the one the request put it at: an
            // enquiry moved on by hand since is at that stage, whatever became
            // of the payment. See `DECLINED_STYLE`.
            const declined =
              enquiry.status === "Payment sent" &&
              enquiry.payment?.status === "Cancelled";

            return (
              <tr
                key={enquiry.id}
                onClick={() => onSelectEnquiry(enquiry)}
                className="border-b border-line last:border-0 hover:bg-[#F8FEFF] transition-colors cursor-pointer"
              >
                <td className="px-4 sm:px-6 py-4">
                  <div className="flex items-center gap-3">
                    <span
                      className={`w-8 h-8 3xl:w-9 3xl:h-9 rounded-full flex items-center justify-center flex-shrink-0 ${tintFor(
                        enquiry.name
                      )}`}
                    >
                      <span className="text-[13px] font-medium">
                        {enquiry.name.charAt(0)}
                      </span>
                    </span>
                    {/* The whole row is clickable for pointers, but the name is a
                        real button so the list stays reachable by keyboard. */}
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation();
                        onSelectEnquiry(enquiry);
                      }}
                      className="text-[14px] text-ink font-medium truncate text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 rounded"
                    >
                      {enquiry.name}
                    </button>
                    {/* Beside the name rather than in the status column: it
                        says nothing about where the enquiry stands, only that
                        nobody here has read it yet, and the name is what the
                        eye lands on first. It keeps its width while the name
                        truncates — a "New" clipped to "Ne" is worse than a
                        shorter name. */}
                    <RowNewTag enquiry={enquiry} />
                  </div>
                </td>
                {/* The same one-line-plus-count reading the detail screen gives
                    an enquiry's goals: whatever fits stays on the line, the
                    rest becomes a "+N" that opens the full list on hover — see
                    `OneLineList`. The cell is a fixed share of a `table-fixed`
                    table, so that is almost always one name here. */}
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink">
                  <OneLineList items={enquiry.goals} label="goals" />
                </td>
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink">
                  {enquiry.mode}
                </td>
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink">
                  {enquiry.requestedOn}
                </td>
                <td className="px-4 sm:px-6 py-4 text-[14px] text-ink">
                  {enquiry.daysAgo}
                </td>
                <td className="px-4 sm:px-6 py-4">
                  <div className="flex items-center justify-between gap-2">
                    {/* A request still out is not a state to report so much as a
                        question to answer, so the cell is the two answers rather
                        than a pill saying the money was asked for — which is
                        already what the row is about. Every other stage has
                        nothing left to ask, and shows its pill. */}
                    {awaiting ? (
                      // The press must not open the enquiry underneath: these
                      // answer the row, they do not follow it.
                      <div
                        className="flex items-center gap-2"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {/* Said for a screen reader, which cannot see that the
                            cell holds a tick and a cross rather than a word. */}
                        <span className="sr-only">Payment sent</span>
                        <PaymentAnswer
                          tone="confirm"
                          label={`Mark the${asked} payment requested from ${enquiry.name} as received`}
                          gate={answerGate}
                          busy={answering === enquiry.id}
                          onClick={() => onConfirmPayment(enquiry)}
                        />
                        <PaymentAnswer
                          tone="cancel"
                          label={`Decline the${asked} payment requested from ${enquiry.name}`}
                          gate={answerGate}
                          busy={answering === enquiry.id}
                          onClick={() => onDeclinePayment(enquiry)}
                        />
                      </div>
                    ) : (
                      <span
                        className={`inline-flex whitespace-nowrap rounded-full px-2.5 py-1 text-[12px] font-medium ${
                          declined ? DECLINED_STYLE : STATUS_STYLES[enquiry.status]
                        }`}
                      >
                        {declined ? "Payment declined" : enquiry.statusLabel}
                      </span>
                    )}
                    <RowActionsMenu
                      label={`Actions for ${enquiry.name}`}
                      actions={[
                        {
                          label: "View details",
                          onSelect: () => onSelectEnquiry(enquiry),
                        },
                        // Greyed rather than dropped while the workspace has
                        // no plan behind it, so the press that comes anyway is
                        // answered: a menu that had quietly lost an entry
                        // would read as this enquiry having no stage left to
                        // move to. Viewing above it stays open — reading the
                        // record changes nothing, and the detail screen it
                        // opens carries these same answers on its own buttons.
                        {
                          label: "Change status",
                          disabled: statusGate.disabled,
                          onSelect: statusGate.disabled
                            ? statusGate.onDisabledClick ?? (() => {})
                            : () => onChangeStatus(enquiry),
                        },
                      ]}
                    />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      )}
    </table>
  </div>
  );

export default EnquiriesTable;
